import { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import {
  getAppBarStyles,
  toolbarStyles,
  getMenuButtonStyles,
  getMenuPaperStyles,
  getMenuItemStyles,
  expandIconStyles,
} from "./styles/NavBarStyles";
import { NavBarLogo } from "./components/NavBarLogo";
import { NavBarTitle } from "./components/NavBarTitle";
import { LogoutButton } from "./components/LogoutButton";
import ThemeToggle from "./components/ThemeToggle";
import { useNavBarCommon } from "./hooks/useNavBarCommon";
import { useAppTheme } from "../../context/theme.context";
import { AuthContext } from "../../context/user.context";
import { useContext } from "react";

const NavBarDeveloper = () => {
  const { currentUser } = useContext(AuthContext);
  const isHeadmaster = currentUser?.contextType === "headmaster";
  const { handleLogout, handleNavigate } = useNavBarCommon();
  const { colors } = useAppTheme();

  const [anchorDb, setAnchorDb] = useState(null);
  const [anchorCash, setAnchorCash] = useState(null);
  const [anchorGroups, setAnchorGroups] = useState(null);
  const openDb = Boolean(anchorDb);
  const openCash = Boolean(anchorCash);
  const openGroups = Boolean(anchorGroups);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuSx = {
    "& .MuiPaper-root": {
      ...getMenuPaperStyles(colors),
      "& .MuiMenuItem-root": {
        ...getMenuItemStyles(colors),
      },
    },
  };

  return (
    <Box sx={{ flexGrow: 1, mb: 3 }}>
      <AppBar position="static" sx={getAppBarStyles(colors)}>
        <Toolbar sx={toolbarStyles}>
          <NavBarLogo onHomeClick={() => handleNavigate("/")} />

          <NavBarTitle
            title={isHeadmaster ? "Panel AutoApp" : "Panel Desarrollador"}
          />

          {!isMobile ? (
            <>
              <Button
                color="inherit"
                onClick={() => handleNavigate("/demo-requests")}
                sx={getMenuButtonStyles(false)}
              >
                Solicitudes Demo
              </Button>

              {isHeadmaster ? (
                <>
                  <Button
                    color="inherit"
                    onClick={() => handleNavigate("/auctions-to-cash-out")}
                    sx={getMenuButtonStyles(false)}
                  >
                    Cobros
                  </Button>
                  <Button
                    color="inherit"
                    onClick={(e) => setAnchorGroups(e.currentTarget)}
                    endIcon={
                      <ExpandMoreIcon sx={expandIconStyles(openGroups)} />
                    }
                    sx={getMenuButtonStyles(openGroups)}
                  >
                    Grupos
                  </Button>
                  <Menu
                    anchorEl={anchorGroups}
                    open={openGroups}
                    onClose={() => setAnchorGroups(null)}
                    sx={menuSx}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    disableAutoFocusItem
                  >
                    <MenuItem
                      onClick={() => {
                        setAnchorGroups(null);
                        handleNavigate("/new-group");
                      }}
                    >
                      Crear Nuevo Grupo
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAnchorGroups(null);
                        handleNavigate("/modify-members");
                      }}
                    >
                      Modificar Miembros
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAnchorGroups(null);
                        handleNavigate("/members-backfill");
                      }}
                    >
                      Miembros (DNI)
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  <Button
                    color="inherit"
                    onClick={(e) => setAnchorDb(e.currentTarget)}
                    endIcon={<ExpandMoreIcon sx={expandIconStyles(openDb)} />}
                    sx={getMenuButtonStyles(openDb)}
                  >
                    Base de Datos
                  </Button>
                  <Menu
                    anchorEl={anchorDb}
                    open={openDb}
                    onClose={() => setAnchorDb(null)}
                    sx={menuSx}
                    transformOrigin={{ horizontal: "left", vertical: "top" }}
                    anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                    disableAutoFocusItem
                  >
                    <MenuItem
                      onClick={() => {
                        setAnchorDb(null);
                        handleNavigate("/car-analytics");
                      }}
                    >
                      Analíticas de Vehículos
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAnchorDb(null);
                        handleNavigate("/active-auction-offers");
                      }}
                    >
                      Ofertas de Subastas Activas
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAnchorDb(null);
                        handleNavigate("/resellers-management");
                      }}
                    >
                      Gestión de Reventas
                    </MenuItem>
                  </Menu>

                  <Button
                    color="inherit"
                    onClick={(e) => setAnchorCash(e.currentTarget)}
                    endIcon={<ExpandMoreIcon sx={expandIconStyles(openCash)} />}
                    sx={getMenuButtonStyles(openCash)}
                  >
                    Cobros
                  </Button>
                  <Menu
                    anchorEl={anchorCash}
                    open={openCash}
                    onClose={() => setAnchorCash(null)}
                    sx={menuSx}
                    transformOrigin={{ horizontal: "left", vertical: "top" }}
                    anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                    disableAutoFocusItem
                  >
                    <MenuItem
                      onClick={() => {
                        setAnchorCash(null);
                        handleNavigate("/auctions-to-cash-out");
                      }}
                    >
                      Subastas a Cobrar
                    </MenuItem>
                  </Menu>

                  <Button
                    color="inherit"
                    onClick={(e) => setAnchorGroups(e.currentTarget)}
                    endIcon={
                      <ExpandMoreIcon sx={expandIconStyles(openGroups)} />
                    }
                    sx={getMenuButtonStyles(openGroups)}
                  >
                    Grupos
                  </Button>
                  <Menu
                    anchorEl={anchorGroups}
                    open={openGroups}
                    onClose={() => setAnchorGroups(null)}
                    sx={menuSx}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    disableAutoFocusItem
                  >
                    <MenuItem
                      onClick={() => {
                        setAnchorGroups(null);
                        handleNavigate("/new-group");
                      }}
                    >
                      Crear Nuevo Grupo
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAnchorGroups(null);
                        handleNavigate("/modify-members");
                      }}
                    >
                      Modificar Miembros
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAnchorGroups(null);
                        handleNavigate("/members-backfill");
                      }}
                    >
                      Miembros (DNI)
                    </MenuItem>
                  </Menu>
                  <Button
                    color="inherit"
                    onClick={() => handleNavigate("/headmasters")}
                    sx={getMenuButtonStyles(false)}
                  >
                    Headmasters
                  </Button>
                </>
              )}
            </>
          ) : (
            <IconButton color="inherit" onClick={() => setMobileMenuOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}

          <ThemeToggle />
          <LogoutButton onLogout={handleLogout} />
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            backgroundColor: colors.surface || "#fff",
            backgroundImage: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 2,
            background: colors.navBar?.gradient || colors.primary,
            color: "#fff",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Panel Desarrollador
          </Typography>
          <IconButton
            size="small"
            onClick={() => setMobileMenuOpen(false)}
            sx={{ color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ pt: 1 }}>
          <ListItemButton
            onClick={() => {
              setMobileMenuOpen(false);
              handleNavigate("/");
            }}
            sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
          >
            <ListItemText
              primary="Inicio"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItemButton>

          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textSecondary || "text.secondary",
                fontWeight: 700,
                fontSize: "0.7rem",
                letterSpacing: 1.2,
              }}
            >
              Comercial
            </Typography>
          </Box>
          <ListItemButton
            onClick={() => {
              setMobileMenuOpen(false);
              handleNavigate("/demo-requests");
            }}
            sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
          >
            <ListItemText primary="Solicitudes Demo" />
          </ListItemButton>

          <Divider sx={{ my: 1.5, mx: 2 }} />

          <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textSecondary || "text.secondary",
                fontWeight: 700,
                fontSize: "0.7rem",
                letterSpacing: 1.2,
              }}
            >
              Base de Datos
            </Typography>
          </Box>
          <ListItemButton
            onClick={() => {
              setMobileMenuOpen(false);
              handleNavigate("/car-analytics");
            }}
            sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
          >
            <ListItemText primary="Analíticas de Vehículos" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              setMobileMenuOpen(false);
              handleNavigate("/active-auction-offers");
            }}
            sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
          >
            <ListItemText primary="Ofertas de Subastas Activas" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              setMobileMenuOpen(false);
              handleNavigate("/resellers-management");
            }}
            sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
          >
            <ListItemText primary="Gestión de Reventas" />
          </ListItemButton>

          <Divider sx={{ my: 1.5, mx: 2 }} />

          <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textSecondary || "text.secondary",
                fontWeight: 700,
                fontSize: "0.7rem",
                letterSpacing: 1.2,
              }}
            >
              Cobros
            </Typography>
          </Box>
          <ListItemButton
            onClick={() => {
              setMobileMenuOpen(false);
              handleNavigate("/auctions-to-cash-out");
            }}
            sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
          >
            <ListItemText primary="Subastas a Cobrar" />
          </ListItemButton>

          <Divider sx={{ my: 1.5, mx: 2 }} />

          <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
            <Typography
              variant="overline"
              sx={{
                color: colors.textSecondary || "text.secondary",
                fontWeight: 700,
                fontSize: "0.7rem",
                letterSpacing: 1.2,
              }}
            >
              Grupos
            </Typography>
          </Box>
          <ListItemButton
            onClick={() => {
              setMobileMenuOpen(false);
              handleNavigate("/new-group");
            }}
            sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
          >
            <ListItemText primary="Crear Nuevo Grupo" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              setMobileMenuOpen(false);
              handleNavigate("/modify-members");
            }}
            sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
          >
            <ListItemText primary="Modificar Miembros" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              setMobileMenuOpen(false);
              handleNavigate("/members-backfill");
            }}
            sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
          >
            <ListItemText primary="Miembros (DNI)" />
          </ListItemButton>
        </List>
      </Drawer>
    </Box>
  );
};

export default NavBarDeveloper;
